// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { buildEvent, formatAmount } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import type { ReconciliationPostedPayload } from '../../types.js';

interface ReconcileViewProps {
  accountId: string;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  onClose: () => void;
}

export function ReconcileView({ accountId, worker, keyData, onClose }: ReconcileViewProps) {
  const { t } = useTranslation(['accounting', 'transactions', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const account = ledger.accounts.get(accountId);

  const [statementDate, setStatementDate] = useState(new Date().toISOString().slice(0, 10));
  const [statementBalance, setStatementBalance] = useState('');
  const [checkedSplitIds, setCheckedSplitIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!account) return null;
  const acc = account;

  const unreconciledItems: {
    txId: string;
    txDate: string;
    txDesc: string;
    txNum: string;
    splitId: string;
    amount: number;
    cleared: boolean;
  }[] = [];

  for (const tx of ledger.transactions.values()) {
    if (tx.voided) continue;
    for (const split of tx.splits) {
      if (split.accountId === accountId && split.reconcileState !== 'y') {
        unreconciledItems.push({
          txId: tx.id,
          txDate: tx.date,
          txDesc: tx.description,
          txNum: tx.num,
          splitId: split.splitId,
          amount: split.amount,
          cleared: split.reconcileState === 'c',
        });
      }
    }
  }
  unreconciledItems.sort((a, b) => a.txDate.localeCompare(b.txDate));

  const statementCents = statementBalance !== '' ? Math.round(parseFloat(statementBalance) * 100) : NaN;
  const clearedCents = unreconciledItems
    .filter((item) => checkedSplitIds.has(item.splitId))
    .reduce((sum, item) => sum + item.amount, 0);

  const diff = isNaN(statementCents) ? NaN : clearedCents - statementCents;
  const balanced = !isNaN(diff) && diff === 0 && statementBalance !== '';

  const deposits = unreconciledItems.filter((i) => i.amount > 0);
  const withdrawals = unreconciledItems.filter((i) => i.amount < 0);

  function toggleSplit(splitId: string) {
    setCheckedSplitIds((prev) => {
      const next = new Set(prev);
      if (next.has(splitId)) next.delete(splitId);
      else next.add(splitId);
      return next;
    });
  }

  function toggleAll() {
    if (checkedSplitIds.size === unreconciledItems.length) {
      setCheckedSplitIds(new Set());
    } else {
      setCheckedSplitIds(new Set(unreconciledItems.map((i) => i.splitId)));
    }
  }

  async function handlePost() {
    if (!balanced || checkedSplitIds.size === 0) return;
    setError('');
    setSaving(true);
    try {
      const payload: ReconciliationPostedPayload = {
        accountId,
        statementDate,
        statementBalance: statementCents,
        reconciledSplitIds: [...checkedSplitIds],
      };
      await encryptAndPush(await buildEvent('ReconciliationPosted', payload, ledger.lastSequence + 1));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:postReconciliation'));
    } finally {
      setSaving(false);
    }
  }

  function ItemRow({ item }: { item: typeof unreconciledItems[0] }) {
    const checked = checkedSplitIds.has(item.splitId);
    return (
      <tr
        className={`border-b border-border cursor-pointer text-sm transition-colors ${
          checked ? 'bg-accent-muted' : 'hover:bg-bg-elevated'
        }`}
        onClick={() => toggleSplit(item.splitId)}
      >
        <td className="py-1.5 px-3">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggleSplit(item.splitId)}
            onClick={(e) => e.stopPropagation()}
            className="border-border"
          />
        </td>
        <td className="py-1.5 px-2 whitespace-nowrap font-mono text-xs text-text-muted">{formatDate(item.txDate, { month: '2-digit', day: '2-digit', year: '2-digit' })}</td>
        <td className="py-1.5 px-2 font-mono text-xs text-text-subtle">{item.txNum}</td>
        <td className="py-1.5 px-2 text-text">{item.txDesc}</td>
        <td className="py-1.5 px-3 text-right font-mono text-text">
          {formatAmount(Math.abs(item.amount), acc.currency)}
        </td>
      </tr>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-text">{t('reconcile.title', { name: acc.name })}</h2>
            <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('reconcile.statementDate')}</label>
              <input
                type="date"
                value={statementDate}
                onChange={(e) => setStatementDate(e.target.value)}
                className="bg-bg border border-border text-text px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('reconcile.statementEndingBalance')}</label>
              <input
                type="number"
                step="0.01"
                value={statementBalance}
                onChange={(e) => setStatementBalance(e.target.value)}
                className="bg-bg border border-border text-text px-3 py-1.5 text-sm focus:border-accent focus:outline-none font-mono w-36"
                placeholder="0.00"
              />
            </div>
            <div className="ml-auto text-right">
              <div className="text-xs text-text-muted mb-0.5">{t('reconcile.clearedBalance')}</div>
              <div className={`text-xl font-mono font-semibold ${balanced ? 'text-positive' : !isNaN(diff) && statementBalance ? 'text-negative' : 'text-text'}`}>
                {formatAmount(clearedCents, acc.currency)}
              </div>
              {statementBalance && !isNaN(diff) && !balanced && (
                <div className="text-xs text-negative">
                  {t('reconcile.differenceWithAmount', { amount: `${diff > 0 ? '+' : ''}${formatAmount(diff, acc.currency)}` })}
                </div>
              )}
              {balanced && <div className="text-xs text-positive font-medium">{t('reconcile.balanced')} ✓</div>}
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Deposits */}
          <div className="flex-1 overflow-y-auto border-r border-border">
            <div className="px-4 py-2 bg-bg-surface border-b border-border flex items-center justify-between">
              <span className="text-xs font-medium text-positive uppercase tracking-wide">
                {t('reconcile.fundsIn')} ({deposits.length})
              </span>
              <span className="text-xs font-mono text-positive">
                {formatAmount(deposits.filter((i) => checkedSplitIds.has(i.splitId)).reduce((s, i) => s + i.amount, 0), acc.currency)}
              </span>
            </div>
            {deposits.length === 0 ? (
              <div className="py-8 text-center text-xs text-text-subtle">{t('reconcile.noDeposits')}</div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-bg-surface border-b border-border text-xs text-text-muted uppercase">
                  <tr>
                    <th className="py-1.5 px-3 w-8">
                      <input type="checkbox"
                        checked={deposits.every((i) => checkedSplitIds.has(i.splitId))}
                        onChange={() => {
                          if (deposits.every((i) => checkedSplitIds.has(i.splitId))) {
                            setCheckedSplitIds((prev) => {
                              const next = new Set(prev);
                              deposits.forEach((i) => next.delete(i.splitId));
                              return next;
                            });
                          } else {
                            setCheckedSplitIds((prev) => new Set([...prev, ...deposits.map((i) => i.splitId)]));
                          }
                        }}
                      />
                    </th>
                    <th className="text-left py-1.5 px-2">{t('columns.date')}</th>
                    <th className="text-left py-1.5 px-2">{t('columns.num')}</th>
                    <th className="text-left py-1.5 px-2">{t('columns.description')}</th>
                    <th className="text-right py-1.5 px-3">{t('columns.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((item) => <ItemRow key={item.splitId} item={item} />)}
                </tbody>
              </table>
            )}
          </div>

          {/* Withdrawals */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-2 bg-bg-surface border-b border-border flex items-center justify-between">
              <span className="text-xs font-medium text-negative uppercase tracking-wide">
                {t('reconcile.fundsOut')} ({withdrawals.length})
              </span>
              <span className="text-xs font-mono text-negative">
                {formatAmount(Math.abs(withdrawals.filter((i) => checkedSplitIds.has(i.splitId)).reduce((s, i) => s + i.amount, 0)), acc.currency)}
              </span>
            </div>
            {withdrawals.length === 0 ? (
              <div className="py-8 text-center text-xs text-text-subtle">{t('reconcile.noWithdrawals')}</div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-bg-surface border-b border-border text-xs text-text-muted uppercase">
                  <tr>
                    <th className="py-1.5 px-3 w-8">
                      <input type="checkbox"
                        checked={withdrawals.every((i) => checkedSplitIds.has(i.splitId))}
                        onChange={() => {
                          if (withdrawals.every((i) => checkedSplitIds.has(i.splitId))) {
                            setCheckedSplitIds((prev) => {
                              const next = new Set(prev);
                              withdrawals.forEach((i) => next.delete(i.splitId));
                              return next;
                            });
                          } else {
                            setCheckedSplitIds((prev) => new Set([...prev, ...withdrawals.map((i) => i.splitId)]));
                          }
                        }}
                      />
                    </th>
                    <th className="text-left py-1.5 px-2">{t('columns.date')}</th>
                    <th className="text-left py-1.5 px-2">{t('columns.num')}</th>
                    <th className="text-left py-1.5 px-2">{t('columns.description')}</th>
                    <th className="text-right py-1.5 px-3">{t('columns.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((item) => <ItemRow key={item.splitId} item={item} />)}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {error && (
          <div className="px-4 py-2 border-t border-border text-negative text-sm">{error}</div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between shrink-0">
          <p className="text-sm text-text-muted">
            {t('reconcile.clearedProgress', { cleared: checkedSplitIds.size, total: unreconciledItems.length })}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
              {t('common:actions.cancel')}
            </button>
            <button onClick={() => { toggleAll(); onClose(); }} className="px-4 py-1.5 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
              {t('reconcile.postpone')}
            </button>
            <button
              onClick={handlePost}
              disabled={!balanced || saving || checkedSplitIds.size === 0}
              className="px-4 py-1.5 bg-positive text-white text-sm font-medium hover:opacity-80 disabled:opacity-40 transition-opacity"
            >
              {saving ? t('reconcile.posting') : t('reconcile.finish')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
